import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import FinishLibrary from "@/components/FinishLibrary";
import { FINISHES, CONTACT } from "@/lib/content";

export function PageHero({ eyebrow, title, body }) {
  return (
    <section className="relative pt-44 pb-16" style={{ background: "var(--ink)" }}>
      <div className="pour-field absolute inset-0 z-0 overflow-hidden">
        <span className="blob" style={{ width: 480, height: 480, top: -160, right: -120, opacity: 0.28, background: "radial-gradient(circle at 40% 35%, var(--brass-bright), var(--copper) 55%, transparent 75%)" }} />
      </div>
      <div className="wrap relative z-[1]">
        <Reveal>
          <div className="flex items-center gap-3 mb-5">
            <span className="inline-block w-9 h-px" style={{ background: "var(--brass-bright)" }} />
            <span className="font-mono-lbl text-[11px]" style={{ color: "var(--brass-bright)" }}>{eyebrow}</span>
          </div>
          <h1 className="font-serif max-w-[820px]" style={{ fontSize: "clamp(38px,5.4vw,72px)", lineHeight: 1.02, color: "var(--champagne)" }}>{title}</h1>
          {body && <p className="mt-6 max-w-[560px] text-[17px] leading-relaxed" style={{ color: "var(--stone)" }}>{body}</p>}
        </Reveal>
      </div>
    </section>
  );
}

export default function FinishesPage() {
  const navigate = useNavigate();
  return (
    <>
      <PageHero
        eyebrow="The Finish Library"
        title="Four systems. Infinite floors."
        body="Every LuxeBond floor begins with one of four resin systems. Each is custom-mixed to your palette, sheen, and traffic load — then hand-worked while the resin is live."
      />

      <section className="pb-16" style={{ background: "var(--ink)" }}>
        <div className="wrap">
          <Reveal><FinishLibrary /></Reveal>
        </div>
      </section>

      <section className="py-24" style={{ background: "var(--ink-2)" }}>
        <div className="wrap">
          <div className="grid gap-px" style={{ background: "var(--line)", border: "1px solid var(--line)" }}>
            {FINISHES.map((f, i) => (
              <Reveal key={f.id} delay={i * 0.05}>
                <div className="grid md:grid-cols-[220px_1fr_auto] gap-8 items-center p-8" style={{ background: "var(--ink)" }} data-testid={`finish-row-${f.id}`}>
                  <div className="relative aspect-[4/3] overflow-hidden" style={{ border: "1px solid var(--line)" }}>
                    <img src={f.image} alt={f.title} className="w-full h-full object-cover" />
                  </div>
                  <div>
                    <div className="flex items-center gap-3">
                      <span className="rounded-full" style={{ width: 22, height: 22, background: f.dot, border: "1px solid rgba(239,230,211,0.25)" }} />
                      <h3 className="font-serif text-2xl" style={{ color: "var(--champagne)" }}>{f.title}</h3>
                    </div>
                    <p className="text-[15px] leading-relaxed mt-3 max-w-[560px]" style={{ color: "var(--stone)" }}>{f.body}</p>
                    <div className="flex flex-wrap gap-2 mt-4">
                      {f.tags.map((t) => (
                        <span key={t} className="text-[10.5px] px-3 py-[6px]" style={{ border: "1px solid var(--line)", color: "var(--stone)" }}>{t}</span>
                      ))}
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 md:min-w-[180px]">
                    {f.specs.map(([k, v]) => (
                      <div key={k} className="flex justify-between gap-6 text-[12px] py-2" style={{ borderTop: "1px solid var(--line)" }}>
                        <span style={{ color: "var(--stone-dim)" }}>{k}</span>
                        <span className="font-mono-lbl !tracking-[0.05em] text-[11px]" style={{ color: "var(--champagne)" }}>{v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <div className="flex flex-wrap gap-4 mt-12">
              <a href={CONTACT.visualizer} target="_blank" rel="noopener noreferrer" className="btn btn-primary" data-testid="finishes-visualizer">
                Design your floor in the Visualizer <ArrowUpRight size={16} />
              </a>
              <button onClick={() => { navigate("/"); setTimeout(() => document.getElementById("quote")?.scrollIntoView({ behavior: "smooth" }), 350); }} className="btn btn-ghost">
                Get a quote <ArrowRight size={16} />
              </button>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
